import React, { useState, useEffect } from "react";
import "../Styles/BlackProfileView.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FiUser } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaRegFileAlt } from "react-icons/fa";
import ApiService from "../nhrc_apis/ApiService"; // ✅ adjust path if needed

const BlackProfileView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const employeeFromList = state?.employee;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);

  useEffect(() => {
    const fetchProfileById = async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ Get profile_id from navigation state
        const profileId =
          employeeFromList?.id ||
          employeeFromList?.profile_id;

        if (!profileId) {
          setError("Profile ID not found.");
          return;
        }

        // ✅ STEP 1 — Fetch full profile by ID
        const response = await ApiService.HrBlackProfilesAPI.getBlackProfileById(profileId);
        const profile = response?.data?.data || response?.data;
        setData(profile);

        // ✅ STEP 2 — Fetch document URL
        try {
          const docResponse = await ApiService.HrBlackProfilesAPI.getDocument(profileId);
          const docData = docResponse?.data;
          const url =
            docData?.document_url ||
            docData?.url ||
            docData?.file_url ||
            docData?.data?.url ||
            null;
          setDocumentUrl(url);
        } catch (docErr) {
          console.warn("DOCUMENT FETCH WARNING:", docErr);
          // don't block the page if doc fails
        }

      } catch (err) {
        console.error("FETCH PROFILE BY ID ERROR:", err);
        setError("Failed to load profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileById();
  }, []);

  const handleBack = () => navigate(-1);

  const copyToClipboard = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    alert("Copied: " + txt);
  };

  // ✅ LOADING
  if (loading) {
    return (
      <div className="nhrc-bpv-page">
        <div className="nhrc-bpv-card">
          <p style={{ color: "#fff", textAlign: "center", padding: "40px" }}>
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  // ✅ ERROR
  if (error || !data) {
    return (
      <div className="nhrc-bpv-page">
        <div className="nhrc-bpv-card">
          <p style={{ color: "red", textAlign: "center", padding: "40px" }}>
            {error || "No employee data found."}
          </p>
          <div style={{ textAlign: "center" }}>
            <button className="nhrc-bpv-back-btn" onClick={handleBack}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Resolve photo — API image_url or uploaded photo
  const photoSrc =
    data.photo_url ||
    data.image_url ||
    data.photo ||
    null;

  return (
    <div className="nhrc-bpv-page">
      <div className="nhrc-bpv-card">

        {/* 🔹 HR Top Bar */}
        <div className="nhrc-bpv-topbar">
          <p className="nhrc-bpv-hrname">
            <FiUser className="nhrc-bpv-hr-icon" />
            HR – {data.hr_name || data.hrName || "Unknown"}
          </p>
          <div className="nhrc-bpv-topbar-actions">
            <button className="nhrc-bpv-back-btn" onClick={handleBack}>
              Back
            </button>
            <BsThreeDotsVertical className="nhrc-bpv-icon-dot" />
          </div>
        </div>

        {/* 🔹 Inner UI */}
        <div className="nhrc-bpv-inner">
          <p className="nhrc-bpv-main-title">Employee Black Profile</p>

          {/* 🔹 Profile */}
          <div className="nhrc-bpv-profile-row">
            <div className="nhrc-bpv-avatar-wrap">
              {photoSrc ? (
                <img src={photoSrc} alt="dp" className="nhrc-bpv-avatar" />
              ) : (
                // ✅ Fallback avatar if no photo
                <div
                  className="nhrc-bpv-avatar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#0a0505",
                    color: "#fff",
                    fontSize: 32,
                  }}
                >
                  <FiUser />
                </div>
              )}
            </div>
            <div className="nhrc-bpv-profile-info">
              <span className="nhrc-bpv-name" style={{color:"white"}}>
                {data.employee_name || data.employeeName || "N/A"}
              </span>
              <span className="nhrc-bpv-title">
                {data.designation || "N/A"}
              </span>
              <span
                className="nhrc-bpv-status-pill"
                style={{
                  background: data.status === "Active" ? "#0f8a33" : "#ff4444"
                }}
              >
                {data.status || "N/A"}
              </span>
            </div>
          </div>

          {/* 🔹 Identifiers */}
          <div className="nhrc-bpv-section pb-3">
            <p className="nhrc-bpv-section-title">Identifiers</p>
            <div className="nhrc-bpv-grid two-cols">
              <div>
                <p className="nhrc-bpv-label">UAN Number</p>
                <p className="nhrc-bpv-value">{data.uan_number || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Employee ID</p>
                <p className="nhrc-bpv-value">{data.employee_id || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Aadhaar</p>
                <p className="nhrc-bpv-value">{data.aadhaar_number || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">PAN Card</p>
                <p className="nhrc-bpv-value">{data.pan_number || "N/A"}</p>
              </div>
            </div>

            <div className="nhrc-bpv-button-row">
              <button
                className="nhrc-bpv-primary-btn"
                onClick={() => copyToClipboard(data.uan_number)}
              >
                Copy UAN
              </button>
              <button className="nhrc-bpv-secondary-btn">Hide Aadhaar</button>
            </div>

            <p className="nhrc-bpv-warning">
              Only authorized admins should view full Aadhaar.
            </p>
          </div>
          <br />

          {/* 🔹 Contact */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Contact</p>
            <div className="nhrc-bpv-grid two-cols">
              <div>
                <p className="nhrc-bpv-label">Email</p>
                <p className="nhrc-bpv-value">{data.email || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Phone</p>
                <p className="nhrc-bpv-value">{data.phone || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Location</p>
                <p className="nhrc-bpv-value">{data.location || "N/A"}</p>
              </div>
            </div>
          </div>
          <br />

          {/* 🔹 Employment */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Employment</p>
            <div className="nhrc-bpv-grid four-cols">
              <div>
                <p className="nhrc-bpv-label">Department</p>
                <p className="nhrc-bpv-value">{data.department || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Experience</p>
                <p className="nhrc-bpv-value">{data.experience || "N/A"}</p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Joining Date</p>
                <p className="nhrc-bpv-value">
                  {data.date_of_joining
                    ? new Date(data.date_of_joining).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="nhrc-bpv-label">Reporting To</p>
                <p className="nhrc-bpv-value">{data.reporting_to || "N/A"}</p>
              </div>
            </div>
          </div>
          <br />

          {/* 🔹 Remarks */}
          <div className="nhrc-bpv-section">
            <p className="nhrc-bpv-section-title">Remarks</p>
            <ul className="nhrc-bpv-remarks-list">
              {Array.isArray(data.remarks)
                ? data.remarks.map((r, i) => <li key={i}>{r}</li>)
                : <li>{data.remarks || "No remarks available"}</li>
              }
            </ul>
          </div>
          <br />

          {/* 🔹 Documents */}
<div className="nhrc-bpv-section">
  <p className="nhrc-bpv-section-title">Documents</p>

  <div className="nhrc-bpv-docs-grid">
    {documentUrl ? (
      <div className="nhrc-bpv-doc-card">
        <FaRegFileAlt className="nhrc-bpv-doc-icon" />

        <span className="nhrc-bpv-doc-title">
          {data.document_name || "Document"}
        </span>

        {/* ✅ FIXED LINK */}
        <a
          href={documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 11,
            color: "#90caf9",
            marginTop: 4,
            display: "block"
          }}
        >
          View Document
        </a>
      </div>
    ) : (
      <p style={{ color: "#bbb" }}>No documents uploaded</p>
    )}
  </div>

  <p className="nhrc-bpv-doc-note">
    Documents are blurred by default. Request to view securely.
  </p>
</div>

          {/* 🔹 PII Warning */}
          <div className="nhrc-bpv-section" style={{ background: "#2a0000" }}>
            <p style={{ fontSize: 12, color: "#ffb4b4", fontWeight: 500, marginTop: "10px" }}>
              This profile contains Personally Identifiable Information (PII).
              Do not share or record. Access is logged.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BlackProfileView;